import { connectRange } from 'instantsearch.js/es/connectors';
import { debounce } from 'instantsearch.js/es/lib/utils/debounce';
import { createWidgetMixin, createSuitMixin, AisRefinementList } from 'vue-instantsearch';
import VueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css';

Vue.component('ais-range-slider', {
  template: "#ais-range-slider",
  components: {
    AisRefinementList,
    VueSlider,
  },
  mixins: [
    createSuitMixin({ name: 'RangeSlider' }),
    createWidgetMixin(
      { connector: connectRange },
      { $$widgetType: 'ais.rangeSlider' }
    ),
  ],
  props: {
    attribute: {
      type: String,
      required: true,
    },
    limit: {
      type: Number,
      required: false,
      default: undefined,
    },
    min: {
      type: Number,
      required: false,
      default: undefined,
    },
    max: {
      type: Number,
      required: false,
      default: undefined,
    },
    precision: {
      type: Number,
      required: false,
      default: 0,
    },
    formatValue: {
      type: Function,
      required: false,
      default: (value) => value,
    },
    stepThresholds: {
      type: Object,
      required: false,
      default: () => ({
        0: 1,
        50: 5,
        100: 10,
        250: 25,
        500: 50,
        1000: 100,
        2500: 250,
        5000: 500,
        10000: 1000,
        25000: 2500,
        50000: 5000,
        100000: 10000,
      }),
    },
    showSuggestions: {
      type: Number,
      required: false,
      default: 6,
    },
    suggestions: {
      type: Array,
      required: false
    },
    histogramBuckets: {
      type: Number,
      required: false,
      default: 0,
    },
    logScale: {
      type: Number,
      required: false,
      default: 0,
    },
    input: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data () {
    return {
      totalCount: 0,
      maxCount: 0,
      rangeStart: null,
      rangeEnd: null,
      values: [],
    };
  },
  computed: {
    widgetParams () {
      return {
        attribute: this.attribute,
        min: this.min,
        max: this.max,
        precision: this.precision,
      };
    },
    range () {
      const { min: rangeMin, max: rangeMax } = this.state?.range ?? {};

      return {
        min: this.min ?? rangeMin,
        max: this.max ?? rangeMax,
      };
    },
    step () {
      return 1 / Math.pow(10, this.precision);
    },
    steps () {
      const { range, stepThresholds } = this;
      const thresholds = Object.keys(stepThresholds).map(Number);
      const stepSizes = Object.values(stepThresholds);

      const steps = [];
      let stepIndex = thresholds.findIndex((threshold) => range.min >= threshold)
      if (stepIndex === -1) stepIndex = thresholds.length - 1;

      for (let i = range.min; i < range.max + stepSizes[stepIndex]; i += stepSizes[stepIndex]) {
        steps.push(i);
        if (stepIndex < thresholds.length && i >= thresholds[stepIndex + 1]) stepIndex++;
      }

      return steps;
    },
    quickSuggestions () {
      const { range, suggestions, showSuggestions } = this;

      return suggestions
        .filter(({ value }) => (value[0] == 0 || value[0] >= range.min) && (value[1] == Infinity || value[1] <= range.max))
        .slice(0, showSuggestions)
        .sort((a, b) => a.value[0] - b.value[0] || a.value[1] - b.value[1])
        .map((item) => ({...item, value: item.value[1] === Infinity ? [item.value[0], this.range.max] : item.value}));
    },
    values () {
      const { steps, state } = this;
      const [minValue, maxValue] = state.start;
      const { min: minRange, max: maxRange } = state.range;

      const min = Math.max(minValue ?? minRange, minRange);
      const max = Math.min(maxValue ?? maxRange, maxRange);

      const minStep = steps.find((step) => step >= min) ?? steps[0];
      const maxStep = steps.find((step) => step >= max) ?? steps[steps.length - 1];

      return [minStep, maxStep];
    },
    histogramThresholds () {
      const { steps, histogramBuckets } = this;

      if (histogramBuckets === 0) return [];

      const interval = Math.floor(steps.length / histogramBuckets);
      let overflow = steps.length % histogramBuckets;

      const thresholds = [];
      for (let i = 0; i < steps.length - 1 && (interval > 0 || overflow > 0); i += interval + (overflow > 0 ? 1 : 0)) {
        thresholds.push(steps[i]);
        overflow--;
      }

      return thresholds;
    },
    debouncedUpdateRanges () {
      return debounce(() => {
        this.updateRange();
      }, 500)
    }
  },
  methods: {
    transformToHistogram (items) {
      if (items.length === 0) return [];

      const { histogramThresholds } = this;

      items = [...items].sort((a, b) => parseFloat(a.value) - parseFloat(b.value));
      const min = parseFloat(items[0].value);
      const max = parseFloat(items[items.length - 1].value);

      this.totalCount = items.reduce((sum, { count }) => sum + count, 0);
      this.maxCount = 0;

      if (histogramThresholds.length === 0) {
        const label = `${min} - ${max} (${items.reduce((sum, { count }) => sum + count, 0)})`;
        this.maxCount = this.totalCount;

        return [{
          label,
          count: this.totalCount,
          highlighted: label,
          value: "0",
          isRefined: false,
        }];
      }

      return histogramThresholds.map((threshold, i) => {
        const nextThreshold = histogramThresholds[i + 1] ?? Infinity;
        const count = items.reduce((sum, { value, count }) => {
          return sum + (+value >= threshold && +value < nextThreshold ? count : 0);
        }, 0);

        const label = nextThreshold === Infinity
          ? `> ${threshold} (${count})`
          : `${threshold} - ${nextThreshold === Infinity ? max : nextThreshold} (${count})`;

        if (count > this.maxCount) this.maxCount = count;

        return {
          label,
          count,
          highlighted: label,
          value: [threshold, nextThreshold === Infinity ? undefined : nextThreshold].join(':'),
          isRefined: false,
        }
      })
    },
    scaleValue (value, max = 1) {
      const { logScale } = this;

      const fraction = value / max;
      return logScale ? Math.log(1 + logScale * fraction) / Math.log(1 + logScale) : fraction;
    },
    updateRange () {
      this.state.refine([this.rangeStart ?? this.values[0], this.rangeEnd ?? this.values[1]])
    },
    updateValues () {
      const { steps, state } = this;

      if (!steps || !state) return;

      const [minValue, maxValue] = state.start;
      const { min: minRange, max: maxRange } = state.range;

      const min = Math.max(minValue ?? minRange, minRange);
      const max = Math.min(maxValue ?? maxRange, maxRange);

      const minStep = steps.find((step) => step >= min) ?? steps[0];
      const maxStep = steps.find((step) => step >= max) ?? steps[steps.length - 1];

      this.values = [minStep, maxStep];
    }
  },
  watch: {
    rangeStart (value) {
      if (value !== this.values[0]) this.debouncedUpdateRanges();
    },
    rangeEnd (value) {
      if (value !== this.values[1]) this.debouncedUpdateRanges();
    },
    values (values) {
      if (!values) return;
      this.rangeStart = values[0];
      this.rangeEnd = values[1];
    },
    steps: {
      handler () {
        this.updateValues();
      },
      immediate: true,
    },
    state: {
      handler () {
        this.updateValues();
      },
      immediate: true,
    },
  },
});
